import { useState, useEffect } from "react";
import { notifications } from "@mantine/notifications";
import {
  getMagicItems,
  postMagicItem,
  deleteMagicItem,
  disenchantMagicItem,
} from "../../requests/magicItem";
import { constellationBlueprint } from "./graph-structure/blueprint";

const tierOptions = [
  { value: "1", label: "Common" },
  { value: "2", label: "Uncommon" },
  { value: "3", label: "Rare" },
  { value: "6", label: "Epic" },
  { value: "15", label: "Wonderous" },
  { value: "30", label: "Vanguard" },
  { value: "50", label: "Legendary" },
  { value: "75", label: "Mythic" },
  { value: "150", label: "Ascendant" },
  { value: "200", label: "Godlike" },
  { value: "300", label: "Terminal" },
  { value: "500", label: "Foundational" },
];

const glyphLookup = new Map(
  constellationBlueprint.quadrants.map((q) => [q.id, q]),
);

const glyphOptions = constellationBlueprint.quadrants.map((q) => ({
  value: q.id,
  label: q.displayName,
}));

const useMagicItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    tier: "",
    glyphId: "",
    crafter: "",
    description: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getMagicItems();
        setItems(
          data.map((it) => ({
            ...it,
            glyph: glyphLookup.get(it.glyphId) ?? null,
          })),
        );
      } catch {
        notifications.show({
          title: "Error",
          message: "Could not load magic items.",
          color: "red",
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const addItem = async () => {
    try {
      const newItem = await postMagicItem(formValues);
      setItems((prev) => [
        ...prev,
        { ...newItem, glyph: glyphLookup.get(newItem.glyphId) ?? null },
      ]);
      closeModal();
      setFormValues({
        name: "",
        tier: "",
        glyphId: "",
        crafter: "",
        description: "",
      });
      notifications.show({
        title: "Added",
        message: "Magic item added to inventory.",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Could not add magic item.",
        color: "red",
      });
    }
  };

  const deleteItem = async (id) => {
    try {
      await deleteMagicItem(id);
      setItems((prev) => prev.filter((it) => it.inventoryId !== id));
      notifications.show({
        title: "Deleted",
        message: "Magic item trashed.",
        color: "gray",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Could not delete magic item.",
        color: "red",
      });
    }
  };

  const disenchantItem = async (id) => {
    try {
      await disenchantMagicItem(id);
      setItems((prev) => prev.filter((it) => it.inventoryId !== id));
      notifications.show({
        title: "Deleted",
        message: "Magic item disenchanted.",
        color: "gray",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Could not disenchant magic item.",
        color: "red",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.currentTarget;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field) => (value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const poolAmount = items.reduce((sum, it) => sum + parseInt(it.tier), 0);

  return {
    items,
    loading,
    modalOpen,
    tierOptions,
    glyphOptions,
    formValues,
    poolAmount,
    addItem,
    deleteItem,
    disenchantItem,
    handleInputChange,
    handleSelectChange,
    openModal,
    closeModal,
  };
};

export default useMagicItems;
